/**
 * Статусная машина версии черновика. §Окружение 7.
 *
 * Это ЕДИНСТВЕННЫЙ список состояний в репозитории. DRAFT_STATUSES из 02-contracts.md §9
 * и набор имён из 03-storage.md (revising, postponed, withdrawn, discarded) удалены:
 * три машины у одного объекта — гарантированное расхождение.
 *
 * Имя `discarded` вернулось 26.08.2026 — и вернулось по процедуре, которую 09 §13.2 для этого
 * и оставила: «снять черновик совсем» это новое состояние и новые рёбра, а не маршрут поверх
 * существующих. Оно НЕ означает удаление строки: `audit_event` ссылается на версию, журнал по
 * NFR-2 живёт не меньше полугода, а цепочка проверяется `npm run audit:verify`. Отброшенный
 * черновик уходит из рабочего списка (`domain/read-model.ts`, `backlog()`), его история
 * остаётся на месте.
 *
 * `enum` запрещён: Node исполняет TypeScript только в режиме стирания, enum даёт
 * ERR_UNSUPPORTED_TYPESCRIPT_SYNTAX в момент первой загрузки файла, а erasableSyntaxOnly
 * ловит это на тайпчеке (TS1294). Поэтому as const-массивы.
 *
 * requires_actor_kind заполняется не для гигиены, а ради FR-4.1: переходы 1, 2, 6
 * (в approved/scheduled) обязаны быть 'human' — ни сервис, ни системный джоб, ни прямой
 * sqlite3 не могут одобрить пост.
 *
 * ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ — автоодобрение (решение Ильи 2026-10-25, docs/ai-lab.md), и в
 * таблице его нет намеренно: строка таблицы — это правило для всех брендов, а исключение
 * действует только для бренда, у которого открыты все три замка. Оно объявлено отдельно
 * (`AUTO_APPROVAL_TRANSITION`), `isTransitionAllowed` его не пропускает, а СУБД пропускает
 * только при включённом выключателе бренда (триггер статуса, миграция бэкенда). Возврат из
 * него — переход 31.
 */

export const DRAFT_STATES = [
  'pending_review',
  'changes_requested',
  'deferred',
  'approved',
  'scheduled',
  'publishing',
  'needs_manual_check',
  'published',
  'expired',
  'failed',
  'retracted',
  'superseded',
  'discarded',
] as const;
export type DraftState = (typeof DRAFT_STATES)[number];

export const ACTOR_KINDS = ['human', 'service', 'system'] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];

/** Единственное допустимое начальное состояние. Держится триггером illegal_initial_status. */
export const INITIAL_DRAFT_STATE: DraftState = 'pending_review';

export const TERMINAL_DRAFT_STATES: readonly DraftState[] = [
  'retracted',
  'superseded',
  'discarded',
];

/** Русские метки для UI и для справочника draft_status в БД. */
export const DRAFT_STATE_LABEL_RU: Readonly<Record<DraftState, string>> = {
  pending_review: 'ожидает',
  changes_requested: 'на переделке',
  deferred: 'отложен',
  approved: 'одобрен',
  scheduled: 'запланирован',
  publishing: 'публикуется',
  needs_manual_check: 'требует проверки',
  published: 'опубликован',
  expired: 'истёк',
  failed: 'ошибка',
  retracted: 'отозван',
  superseded: 'заменён',
  discarded: 'отброшен',
};

export interface TransitionDef {
  /** Номер из таблицы §Окружение 7.2. Ссылки «переход N» по всему репозиторию ведут сюда. */
  readonly no: number;
  readonly from: DraftState;
  readonly to: DraftState;
  /** null = ограничения по виду актора нет. */
  readonly requiresActorKind: ActorKind | null;
  readonly note: string;
}

/** Всё, чего нет в этой таблице, запрещено. */
export const DRAFT_TRANSITIONS: readonly TransitionDef[] = [
  {
    no: 1,
    from: 'pending_review',
    to: 'approved',
    requiresActorKind: 'human',
    note: 'одобрить без даты',
  },
  {
    no: 2,
    from: 'pending_review',
    to: 'scheduled',
    requiresActorKind: 'human',
    note: 'одобрить с датой — здесь выпускается ключ',
  },
  {
    no: 3,
    from: 'pending_review',
    to: 'changes_requested',
    requiresActorKind: 'human',
    note: 'вернуть на переделку с промптом',
  },
  { no: 4, from: 'pending_review', to: 'deferred', requiresActorKind: 'human', note: 'отложить' },
  // Переходы 5, 9, 12 и 13 («пришла версия v+1») не требуют конкретного актора: автором
  // следующей версии может быть и сервис генерации, и человек — редактор исходника появился
  // решением Ильи 24.08. Раньше здесь стояло 'service', и правка человеком записывалась в
  // last_actor_kind как «сервис», иначе триггер откатил бы переход. FR-4.1 это не задевает:
  // он держится на переходах 1, 2 и 6 (одобрение), а `superseded` терминален — исходящих
  // рёбер у него нет, и last_actor_kind этой строки больше никогда не проверяется.
  {
    no: 5,
    from: 'pending_review',
    to: 'superseded',
    requiresActorKind: null,
    note: 'пришла версия v+1',
  },
  {
    no: 6,
    from: 'approved',
    to: 'scheduled',
    requiresActorKind: 'human',
    note: 'назначить слот, выпустить ключ',
  },
  {
    no: 7,
    from: 'approved',
    to: 'changes_requested',
    requiresActorKind: 'human',
    note: 'передумал',
  },
  { no: 8, from: 'approved', to: 'deferred', requiresActorKind: 'human', note: 'отложить' },
  {
    no: 9,
    from: 'approved',
    to: 'superseded',
    requiresActorKind: null,
    note: 'пришла версия v+1',
  },
  {
    no: 10,
    from: 'deferred',
    to: 'pending_review',
    requiresActorKind: 'human',
    note: 'вернуть в работу',
  },
  {
    no: 11,
    from: 'deferred',
    to: 'changes_requested',
    requiresActorKind: 'human',
    note: 'сразу на переделку',
  },
  {
    no: 12,
    from: 'deferred',
    to: 'superseded',
    requiresActorKind: null,
    note: 'пришла версия v+1',
  },
  {
    no: 13,
    from: 'changes_requested',
    to: 'superseded',
    requiresActorKind: null,
    note: 'замыкание ветки переделки: пришла v+1 по этому rework-запросу',
  },
  {
    no: 14,
    from: 'scheduled',
    to: 'approved',
    requiresActorKind: 'human',
    note: 'снять со слота, ключ отзывается, nonce не тратится',
  },
  {
    no: 15,
    from: 'scheduled',
    to: 'publishing',
    requiresActorKind: 'service',
    note: 'захват: только CAS лизинга в транзакции бэкенда',
  },
  {
    no: 16,
    from: 'scheduled',
    to: 'expired',
    requiresActorKind: 'system',
    note: 'ключ протух или окно свежести вышло',
  },
  {
    no: 17,
    from: 'scheduled',
    to: 'failed',
    requiresActorKind: 'system',
    note: 'провал предотправочной проверки',
  },
  {
    no: 18,
    from: 'publishing',
    to: 'published',
    requiresActorKind: 'service',
    note: 'Telegram вернул Message',
  },
  {
    no: 19,
    from: 'publishing',
    to: 'failed',
    requiresActorKind: null,
    note: 'определённый отказ единственной попытки либо истёк лизинг',
  },
  {
    no: 20,
    from: 'publishing',
    to: 'needs_manual_check',
    requiresActorKind: null,
    note: 'исход отправки неизвестен',
  },
  {
    no: 21,
    from: 'needs_manual_check',
    to: 'published',
    requiresActorKind: 'human',
    note: 'проверил канал: пост есть; ОБЯЗАН ввести message_id',
  },
  {
    no: 22,
    from: 'needs_manual_check',
    to: 'failed',
    requiresActorKind: 'human',
    note: 'проверил канал: поста нет',
  },
  {
    no: 23,
    from: 'expired',
    to: 'pending_review',
    requiresActorKind: 'human',
    note: 'вернуть на обновление',
  },
  {
    no: 24,
    from: 'failed',
    to: 'pending_review',
    requiresActorKind: 'human',
    note: 'разобрать и переделать',
  },
  {
    no: 25,
    from: 'failed',
    to: 'scheduled',
    requiresActorKind: 'human',
    note: 'новый слот + новый ключ и новый nonce',
  },
  {
    no: 26,
    from: 'published',
    to: 'retracted',
    requiresActorKind: 'service',
    note: 'подтверждено удаление сообщения ботом',
  },
  // ── Рёбра в `discarded` (26.08.2026, кнопка «Удалить» в бэклоге) ───────────────────────
  //
  // Все четыре — только 'human', и это то же правило, что у одобрения: отбросить черновик
  // значит принять решение о содержании, а такие решения принимает человек. Сервису
  // генерации ребра сюда не дано намеренно — иначе он мог бы гасить собственные неудачные
  // версии, и в бэклоге переставало бы быть видно, что он их делал.
  //
  // Исходных состояний ровно четыре — те, где пост ещё никуда не уехал. Остальные классы
  // закрыты по разным причинам, и ни одна из них не «забыли»:
  //
  //   scheduled                — слот занят и ключ выпущен под конкретное содержание;
  //                              отбросить, не отозвав ключ, значит оставить публикатору
  //                              действующий ключ на мёртвую версию. Путь есть и он один:
  //                              переход 14 (снять со слота) и уже потом отбросить.
  //   published                — пост в канале; убирается отзывом (переход 26), а не
  //                              вычёркиванием из списка: подписчики его уже видели.
  //   publishing               — исход отправки ещё неизвестен; решение по нему принимает
  //                              публикатор (18/19/20), а не человек в интерфейсе.
  //   needs_manual_check,      — аварийные состояния: их РАЗБИРАЮТ (21/22/23/24/25), и
  //   failed, expired            отбрасывание здесь спрятало бы аварию вместо разбора.
  //   retracted, superseded    — уже терминальны; исходящих рёбер у терминального состояния
  //                              не бывает по определению.
  {
    no: 27,
    from: 'pending_review',
    to: 'discarded',
    requiresActorKind: 'human',
    note: 'отбросить: уходит из бэклога, история остаётся',
  },
  {
    no: 28,
    from: 'changes_requested',
    to: 'discarded',
    requiresActorKind: 'human',
    note: 'отбросить, не дожидаясь версии v+1 по этому rework-запросу',
  },
  {
    no: 29,
    from: 'deferred',
    to: 'discarded',
    requiresActorKind: 'human',
    note: 'отбросить отложенное вместо бессрочного ожидания',
  },
  {
    no: 30,
    from: 'approved',
    to: 'discarded',
    requiresActorKind: 'human',
    note: 'передумал после одобрения; слота и ключа здесь ещё нет',
  },
  // ── Возврат из автоодобрения (2026-10-25, docs/ai-lab.md) ─────────────────────────────
  //
  // Админ выключил автоодобрение бренда: каждый автоодобренный пост, чья работа ещё не выдана
  // публикатору, теряет работу, ключ и слот и возвращается НА СОГЛАСОВАНИЕ — к человеку, а не
  // в `approved` (переход 14): одобрения человека у такого поста не было никогда, и «снять со
  // слота» оставило бы его одобренным без решения человека. Делает это бэкенд в той же
  // транзакции, что пишет выключатель, поэтому актор — 'system'. Пост, уже выданный
  // публикатору (`publishing`), не трогается: его исход решает публикатор (18/19/20).
  {
    no: 31,
    from: 'scheduled',
    to: 'pending_review',
    requiresActorKind: 'system',
    note: 'вернуть на согласование (выключено автоодобрение)',
  },
];

/**
 * Исключение из FR-4.1 для автоодобрения — ребро перехода 2, пройденное НЕ человеком.
 *
 * Не строка `DRAFT_TRANSITIONS` и не повод ослабить `isTransitionAllowed`: таблица — правило
 * для всех брендов, а это ребро законно только у бренда, где открыты все три замка (конфиг
 * плюс включённый выключатель, демон подписи, публикатор). Решает здесь не код, а СУБД:
 * триггер статуса пропускает переход с `last_actor_kind = 'system'` только пока строка
 * `auto_approval:<бренд>` включена. Переходы 1 и 6 исключения не имеют вовсе.
 */
export const AUTO_APPROVAL_TRANSITION = {
  no: 2,
  from: 'pending_review',
  to: 'scheduled',
  actorKind: 'system',
} as const satisfies {
  readonly no: number;
  readonly from: DraftState;
  readonly to: DraftState;
  readonly actorKind: ActorKind;
};

/** Это ровно ребро автоодобрения — и ничего шире: другой переход или другой актор — нет. */
export function isAutoApprovalTransition(
  from: DraftState,
  to: DraftState,
  actorKind: ActorKind,
): boolean {
  return (
    from === AUTO_APPROVAL_TRANSITION.from &&
    to === AUTO_APPROVAL_TRANSITION.to &&
    actorKind === AUTO_APPROVAL_TRANSITION.actorKind
  );
}

const TRANSITION_INDEX: ReadonlyMap<string, TransitionDef> = new Map(
  DRAFT_TRANSITIONS.map((t) => [`${t.from}>${t.to}`, t]),
);

export function findTransition(from: DraftState, to: DraftState): TransitionDef | undefined {
  return TRANSITION_INDEX.get(`${from}>${to}`);
}

export function isTransitionAllowed(
  from: DraftState,
  to: DraftState,
  actorKind: ActorKind,
): boolean {
  const t = TRANSITION_INDEX.get(`${from}>${to}`);
  if (t === undefined) return false;
  return t.requiresActorKind === null || t.requiresActorKind === actorKind;
}

export function isDraftState(s: string): s is DraftState {
  return (DRAFT_STATES as readonly string[]).includes(s);
}

export function isActorKind(s: string): s is ActorKind {
  return (ACTOR_KINDS as readonly string[]).includes(s);
}
